import React from 'react';
import { useHistory } from 'react-router-dom';

import {
  SalesByHourOfTheDay,
  SalesSummary,
} from '@/components/organisms/Sales';
import {
  ORDER_FILTER_FORM_ID,
  SalesErrorCard,
  SalesParamsForm,
  SalesTransition,
} from '@/components/organisms/Sales';
import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmSimpleGrid,
} from '@/components/uiParts';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { SalesByDayOfTheWeek } from '@/pages/sales_report/components/SalesByDayOfTheWeek';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useDeliveryServiceLinkEnterprise } from '@/services/Enterprise/DeliveryService/hooks';
import { useEnterprise } from '@/services/Enterprise/hooks';
import { useSalesReportFilter } from '@/services/SalesReport/hooks';

import { SalesCard } from './const';

const SalesPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe: me } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('sales_report', history, me);
  const { salesReport, isLoading, error, filter, setFilter } =
    useSalesReportFilter();

  if (error) {
    errorHandler({ error });
  }

  const {
    enterprise,
    isLoading: isEnterpriseLoading,
    isError: isEnterpriseError,
  } = useEnterprise();
  const {
    deliveryServices,
    isLoading: isDeliveryServiceLoading,
    isError: isDeliveryServiceError,
  } = useDeliveryServiceLinkEnterprise();

  const { noData } = SalesCard;

  const salesSummary = {
    totalPrice: salesReport ? salesReport.total_sales_summary?.total_sales : 0,
    orderCount: salesReport
      ? salesReport.total_sales_summary?.total_order_count
      : 0,
  };

  const { targetsHeight } = useTargetsDomHeight([
    HEADER_ID,
    ORDER_FILTER_FORM_ID,
  ]);

  return (
    <PageLayout title={routes.SalesReport.title}>
      <CmBox>
        {enterprise && deliveryServices && (
          <SalesParamsForm
            filter={filter}
            enterprise={enterprise}
            deliveryServices={deliveryServices}
            handleSubmit={setFilter}
            orderCount={salesSummary?.orderCount ?? 0}
            page="sales"
          />
        )}

        <CmScrollBox w="100%" height={`calc(100vh - ${targetsHeight}px)`}>
          {salesSummary?.totalPrice > 0 ? (
            <CmBox mt={2}>
              <SalesSummary salesSummary={salesSummary} />
              <CmBox mt={3} fontSize="14px">
                ※ 自社配達の場合とCamel
                Orderの注文の場合は、売上金額に配送料を含みます
              </CmBox>
              <CmBox mt={5}>
                {<SalesTransition salesReport={salesReport} />}
              </CmBox>
              <CmSimpleGrid
                columns={{ base: 1, md: 2 }}
                spacingX={3}
                spacingY={5}
                my={5}
              >
                {
                  <SalesByDayOfTheWeek
                    salesWeek={salesReport.sales_by_day_of_week}
                  />
                }
                <SalesByHourOfTheDay
                  salesHour={salesReport.sales_by_time_period}
                />
              </CmSimpleGrid>
            </CmBox>
          ) : (
            !isLoading &&
            !isEnterpriseLoading && (
              <CmMessageCard margin="100px auto 0">
                {noData.message}
              </CmMessageCard>
            )
          )}
        </CmScrollBox>
      </CmBox>

      {(isLoading || isEnterpriseLoading || isDeliveryServiceLoading) && (
        <CmLongLoading />
      )}

      {(isEnterpriseError || isDeliveryServiceError) && <SalesErrorCard />}
    </PageLayout>
  );
};

export default SalesPage;
